import { useState, useEffect, useCallback, useRef, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════════════
   Data Model
   ═══════════════════════════════════════════════════════════════════ */

interface Note {
  id: string;
  title: string;
  content: string;
  folder: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface ArtifactLink {
  title: string;
  url: string;
  group: string;
  note: string;
}

const FOLDERS = ["Daily", "Projects", "Ideas", "Archive"] as const;
type Folder = (typeof FOLDERS)[number];

const STORAGE_KEY = "opentool_notebook_notes";

const ARTIFACT_LINKS: ArtifactLink[] = [
  {
    title: "Instrumentation Milestone",
    url: "/internal/research/instrumentation-milestone.html",
    group: "Milestone",
    note: "Standing tracker for execution evidence, proof model, and next build phases.",
  },
  {
    title: "Flow State Priority Readout",
    url: "/internal/research/flow-state-priority-readout-2026-04-05.html",
    group: "Research",
    note: "Current recommendation on where time is best spent: drain rate, proof model, and operator control.",
  },
  {
    title: "Recent Research Digest",
    url: "/internal/research/recent-research-digest-2026-04-05.html",
    group: "Research",
    note: "Consolidated recent research, contract, milestone, and priority links for direct email and review.",
  },
  {
    title: "Flow State Feedback Loop",
    url: "/internal/research/flow-state-feedback-loop.html",
    group: "Research",
    note: "Capture real interaction moments, attach judgment, and reinforce the patterns that preserve Flow State.",
  },
  {
    title: "Registry Airgap",
    url: "/internal/research/opentool-registry-airgap.html",
    group: "Boundary",
    note: "Closed ingress path for external files: controlled cache, quarantine, admin review, and governed promotion.",
  },
  {
    title: "Commercial Readiness Office",
    url: "/internal/research/commercial-readiness-office.html",
    group: "Operations",
    note: "Proactive commercialization structure covering SRE, compliance, legal/IP, product-policy readiness, and GTM.",
  },
  {
    title: "Slack Control Surface",
    url: "/internal/research/slack-control-surface.html",
    group: "Comms",
    note: "Slack as a governed command and status surface with explicit file-ingress denial and identity-mapped authority.",
  },
  {
    title: "Autopilot Outcome Contract Standard",
    url: "/internal/research/autopilot-outcome-contract-standard.html",
    group: "Autopilot",
    note: "Codifies the `adc:` drafting flow so high-level asks become complete, approval-ready contracts with explicit outcome, proof, delivery, and completion rules.",
  },
  {
    title: "Threat Surface Coverage",
    url: "/docs/architecture/package-of-work-threat-surface-coverage.md",
    group: "Security",
    note: "Requires every governed package of work to say whether relevant threat surfaces are covered, partially covered, or still unknown.",
  },
  {
    title: "Interaction Mode Standard",
    url: "/internal/research/interaction-mode-standard.html",
    group: "UX",
    note: "Defines Flow, Draft, Sandbox, Review, and Observe plus stage controls so ideas can be explored, promoted, or dropped cleanly.",
  },
  {
    title: "Center Drift Signal Model",
    url: "/internal/research/center-drift-signal-model.html",
    group: "Signals",
    note: "Detects task barrage, lane collision, policy-bypass pressure, and other relative indicators of drift away from Flow State.",
  },
  {
    title: "Project Deadman Switch Plan",
    url: "/docs/architecture/project-deadman-switch-plan.md",
    group: "Security",
    note: "Plan-only project-wide shutdown path that stops known services without any destroy behavior and preserves explicit recovery.",
  },
  {
    title: "Experimental Workspace Forks",
    url: "/internal/research/experimental-workspace-forks.html",
    group: "Sessions",
    note: "Conversation/workspace fork model for experiments with the same policy boundary and explicit save, promote, return, and kill paths.",
  },
  {
    title: "Trust Claim Testing Requirements",
    url: "/docs/architecture/trust-claim-testing-requirements.md",
    group: "Testing",
    note: "Testing amendment for trust contracts and claims, including tool-gap classification, falsification, and honest boundary labeling.",
  },
  {
    title: "Session Compaction Prep",
    url: "/internal/research/session-compaction-prep-2026-04-05-flow-state-packages.html",
    group: "Ceremony",
    note: "Captures the latest high-signal refinement: plan-bound packages need visible threat-surface coverage, plus an honest Autopilot background readout.",
  },
  {
    title: "Open Lane Checkpoints",
    url: "/internal/research/open-lane-checkpoints-2026-04-05.html",
    group: "Ceremony",
    note: "Stable checkpoint pass across the currently open lanes so the main thread can return to one centered track.",
  },
  {
    title: "Company Wiki",
    url: "/wiki/",
    group: "Wiki",
    note: "Single intranet home for processes, meetings, deadlines, projects, and documentation, with a read-only preview surface.",
  },
  {
    title: "RFPs Hub",
    url: "/internal/rfps/",
    group: "RFP",
    note: "Layered control stack packet with architecture diagram, invention disclosure, and counsel memo for the mandatory OpenTool routing model.",
  },
  {
    title: "Internal Operating Hub",
    url: "/internal/",
    group: "Internal",
    note: "Top-level company operating surface for research, architecture, and lane routing.",
  },
  {
    title: "Draft Review Surface",
    url: "/internal/drafts/",
    group: "Internal",
    note: "Review-ready checkpoints before promotion into more stable surfaces.",
  },
  {
    title: "Stop Governance Contract",
    url: "/docs/architecture/stop-governance-contract.md",
    group: "Contract",
    note: "Defines reason, authority, mechanism, and surface for stop behavior.",
  },
  {
    title: "Human Self-Protection Governance",
    url: "/docs/architecture/human-self-protection-governance.md",
    group: "Contract",
    note: "Protects the human from unsafe delegation, ambiguity, and trust collapse.",
  },
  {
    title: "Human-Agent Contract",
    url: "/docs/architecture/human-agent-contract.md",
    group: "Contract",
    note: "Defines the operating relationship between humans, bounded runtimes, and worker lanes.",
  },
];

function generateId(): string {
  return `note_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/* ── Demo notes ── */
function createDemoNotes(): Note[] {
  const now = new Date();
  const d = (daysAgo: number) => {
    const dt = new Date(now);
    dt.setDate(dt.getDate() - daysAgo);
    return dt.toISOString();
  };

  return [
    {
      id: "note_demo_1",
      title: "Architecture Decision: Three Ring Model",
      content: `# Architecture Decision: Three Ring Model

## Context

OpenTool follows a **three-ring architecture** that governs how every request flows through the system:

1. **RBAC Ring** — Role-based access control. Every entity (developer, agent, pipeline, service account) is assigned roles. Permissions are evaluated before anything executes.
2. **Routing Ring** — Determines which provider, model, or tool handles the request. Supports load balancing, failover, and cost-aware routing.
3. **Universal Bridge** — The translation layer. Converts between provider APIs so tools and agents can be swapped without code changes.

## Key Decisions

- All three rings are **composable** — you can use RBAC standalone, or pair routing + bridge without RBAC
- The bridge uses [[OpenTelemetry Alignment]] patterns for observability
- Entity model supports teams, not just individuals — see [[Entity Model Design]]

## Open Questions

- [ ] Should we expose ring configuration via UI or keep it config-only?
- [ ] How do we handle cross-ring caching?
- [x] Decided: bridge protocol will be gRPC internally, REST externally

#architecture #core #decision`,
      folder: "Projects",
      tags: ["architecture", "core", "decision"],
      createdAt: d(14),
      updatedAt: d(2),
    },
    {
      id: "note_demo_2",
      title: "Entity Model Design",
      content: `# Entity Model Design

The entity model is the foundation of OpenTool's access control. Every actor in the system is an **entity** with assignable roles.

## Entity Types

| Type | Description |
|------|-------------|
| Developer | Human user with interactive access |
| Agent | AI agent (Claude, GPT, etc.) |
| Pipeline | CI/CD or automation workflow |
| Service Account | Machine-to-machine auth |
| Team | Group of entities with shared roles |
| Host | Physical or virtual machine |

## Role Assignment

Roles are **hierarchical** and support inheritance:

\`\`\`yaml
roles:
  admin:
    permissions: ["*"]
  developer:
    permissions: ["read", "write", "execute"]
    inherits: ["viewer"]
  viewer:
    permissions: ["read"]
\`\`\`

## Integration with IDP

Entities are auto-provisioned from corporate directory via SAML/OIDC/SCIM. See [[IDP Authentication Setup]] for details.

The [[Architecture Decision: Three Ring Model]] relies on this model for the RBAC ring.

#architecture #rbac #entity`,
      folder: "Projects",
      tags: ["architecture", "rbac", "entity"],
      createdAt: d(12),
      updatedAt: d(5),
    },
    {
      id: "note_demo_3",
      title: "IDP Authentication Setup",
      content: `# IDP Authentication Setup

## Priority: P0

Authentication is the first thing that must work in the control plane. No features ship without auth.

## Supported Protocols

- **SAML 2.0** — Enterprise SSO (Okta, Azure AD, OneLogin)
- **OIDC** — Modern auth flow (Google, GitHub, Auth0)
- **SCIM** — Directory sync for auto-provisioning entities
- **API Keys** — For service accounts and CI/CD

## Flow

1. User hits control plane
2. Redirected to IDP (SAML/OIDC)
3. IDP returns assertion/token
4. Control plane validates, creates session
5. Entity auto-provisioned if not exists (via SCIM or JIT)

## Notes

- Session tokens are JWTs with 1hr expiry
- Refresh tokens stored server-side only
- All auth events logged for [[Architecture Decision: Three Ring Model]] audit trail

#auth #security #p0`,
      folder: "Projects",
      tags: ["auth", "security", "p0"],
      createdAt: d(10),
      updatedAt: d(3),
    },
    {
      id: "note_demo_4",
      title: "Sprint Standup - Week 12",
      content: `# Sprint Standup - Week 12

## Yesterday
- Shipped access control workbench UI
- Fixed chat provider switching bug
- Reviewed PR for drift detection

## Today
- Build notebook workbench (this!)
- Wire up graph view for note linking
- Review benchmark results from k6 runs

## Blockers
- None currently

## Notes
- The [[Architecture Decision: Three Ring Model]] is driving most of the current sprint work
- Need to schedule a review of the [[Entity Model Design]] with the team
- /ai summarize the key blockers from last 3 sprints

#daily #standup #sprint`,
      folder: "Daily",
      tags: ["daily", "standup", "sprint"],
      createdAt: d(1),
      updatedAt: d(0),
    },
    {
      id: "note_demo_5",
      title: "OpenTelemetry Alignment",
      content: `# OpenTelemetry Alignment

## Strategy

OpenTool's open-source foundation follows the **OTEL playbook**:

1. Define an open spec (like OTLP)
2. Build reference implementations
3. Let vendors build on top
4. Community drives adoption

## What We Adopt from OTEL

- **Span-based tracing** for every agent action
- **Metrics collection** with Prometheus-compatible export
- **Log correlation** across tools and providers
- **Context propagation** through the [[Architecture Decision: Three Ring Model]] rings

## Differences from OTEL

- We add **cost tracking** as a first-class signal (not just traces/metrics/logs)
- **Governance events** (approvals, denials, quarantine) are a new signal type
- AI-specific attributes: token counts, model versions, prompt hashes

> "We're building the OTEL of AI tooling" - internal framing

#strategy #otel #open-source`,
      folder: "Ideas",
      tags: ["strategy", "otel", "open-source"],
      createdAt: d(8),
      updatedAt: d(4),
    },
    {
      id: "note_demo_6",
      title: "Browser-as-OS Distribution Model",
      content: `# Browser-as-OS Distribution Model

## Core Idea

The browser **is** the operating system. No Electron. No desktop app.

### Architecture

- **Frontend**: GitHub Pages (static, free hosting)
- **Backend**: Local binary that runs on the user's machine
- **Communication**: WebSocket bridge between browser and local agent

### Why This Works

1. Zero install friction — just open a URL
2. GitHub Pages = free hosting forever
3. Local binary handles sensitive operations (file access, shell, etc.)
4. Works offline once cached (PWA)

### Distribution

- Repos used for package storage (GitHub Releases)
- Auto-update via binary self-update mechanism
- See [[Architecture Decision: Three Ring Model]] for how auth works in this model

#distribution #architecture #browser`,
      folder: "Ideas",
      tags: ["distribution", "architecture", "browser"],
      createdAt: d(6),
      updatedAt: d(1),
    },
    {
      id: "note_demo_7",
      title: "Playwright Testing Strategy",
      content: `# Playwright Testing Strategy

## Rule: Every UI change needs a Playwright test. No exceptions.

### Current Coverage

- [x] Desktop window management
- [x] Chat workbench — send/receive
- [x] File explorer — tree navigation
- [x] Settings panel
- [ ] Notebook workbench (WIP)
- [ ] Access control matrix
- [ ] Cost ledger charts

### Pipeline Idea

Auto-convert Playwright tests to k6 load tests:

\`\`\`
Playwright (functional) → k6 script (load) → Grafana Cloud (monitoring)
\`\`\`

This gives us load testing for free from our existing test suite.

### Test Structure

\`\`\`typescript
test('notebook: create and edit note', async ({ page }) => {
  await page.goto('/');
  await page.click('[data-testid="app-notebook"]');
  await page.click('[data-testid="new-note"]');
  // ...assertions
});
\`\`\`

#testing #playwright #qa`,
      folder: "Projects",
      tags: ["testing", "playwright", "qa"],
      createdAt: d(4),
      updatedAt: d(0),
    },
    {
      id: "note_demo_8",
      title: "Agent Team Compositions",
      content: `# Agent Team Compositions

## Concept

Every OpenTool plugin ships with a **default team composition** — a set of agents with defined personas, roles, and counts.

## Example: Code Review Plugin

\`\`\`json
{
  "plugin": "code-review",
  "team": [
    { "persona": "Security Auditor", "role": "reviewer", "count": 1 },
    { "persona": "Performance Analyst", "role": "reviewer", "count": 1 },
    { "persona": "Code Quality Lead", "role": "approver", "count": 1 }
  ]
}
\`\`\`

## How It Ties to RBAC

- Each agent in the team gets an entity in the [[Entity Model Design]]
- Roles from the team config map to RBAC permissions
- The [[Architecture Decision: Three Ring Model]] governs what each agent can access

## Open Ideas

- Let users customize team compositions per project
- Support "specialist" agents that only activate for certain file types
- /ai brainstorm other team compositions for a deployment plugin

#agents #teams #plugins`,
      folder: "Ideas",
      tags: ["agents", "teams", "plugins"],
      createdAt: d(3),
      updatedAt: d(1),
    },
  ];
}

/* ═══════════════════════════════════════════════════════════════════
   Storage helpers
   ═══════════════════════════════════════════════════════════════════ */

function loadNotes(): Note[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const demo = createDemoNotes();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(demo));
  return demo;
}

function saveNotes(notes: Note[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
}

/* ═══════════════════════════════════════════════════════════════════
   Markdown renderer (enhanced with wiki links, tags, checkboxes, AI)
   ═══════════════════════════════════════════════════════════════════ */

function renderMarkdownToHtml(md: string, onWikiLink?: (title: string) => void): string {
  let html = md
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Headers
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold & italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Blockquotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
    // Wiki links
    .replace(/\[\[([^\]]+)\]\]/g, '<span class="nb-wiki-link" data-link="$1">$1</span>')
    // Tags (but not inside HTML attributes or code)
    .replace(/(^|\s)#([a-zA-Z][\w-]*)/gm, '$1<span class="nb-tag-inline">#$2</span>')
    // Checkboxes
    .replace(/^- \[x\] (.+)$/gm, '<div class="nb-checkbox"><input type="checkbox" checked disabled /> $1</div>')
    .replace(/^- \[ \] (.+)$/gm, '<div class="nb-checkbox"><input type="checkbox" disabled /> $1</div>')
    // AI blocks
    .replace(/\/ai (\w+)(.*)/gm, (_match, cmd: string, rest: string) =>
      `<div class="nb-ai-block"><div class="nb-ai-block__header"><svg class="nb-ai-block__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a10 10 0 100 20 10 10 0 000-20z"/><path d="M12 8v8M8 12h8"/></svg>AI: ${cmd}</div><div class="nb-ai-block__content">${rest.trim() || '<span class="nb-typing-dots"><span>.</span><span>.</span><span>.</span></span>'}</div></div>`
    )
    // Markdown links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    // Tables (simple)
    .replace(/^\|(.+)\|$/gm, (match) => {
      const cells = match.split('|').filter(Boolean).map(c => c.trim());
      if (cells.every(c => /^[-:]+$/.test(c))) return ''; // separator row
      const tag = 'td';
      return '<tr>' + cells.map(c => `<${tag}>${c}</${tag}>`).join('') + '</tr>';
    })
    // Unordered lists
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // Horizontal rules
    .replace(/^---$/gm, '<hr />')
    // Paragraphs
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />');

  // Wrap loose li in ul
  html = html.replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>');
  html = html.replace(/<\/ul>\s*<ul>/g, '');
  // Wrap loose tr in table
  html = html.replace(/(<tr>[\s\S]*?<\/tr>)/g, '<table>$1</table>');
  html = html.replace(/<\/table>\s*<table>/g, '');
  // Collapse consecutive blockquotes
  html = html.replace(/<\/blockquote>\s*<blockquote>/g, '<br />');

  return `<p>${html}</p>`;
}

/* ═══════════════════════════════════════════════════════════════════
   Extract helpers
   ═══════════════════════════════════════════════════════════════════ */

function extractHeadings(content: string): { level: number; text: string }[] {
  const result: { level: number; text: string }[] = [];
  for (const line of content.split("\n")) {
    const m = line.match(/^(#{1,4})\s+(.+)$/);
    if (m) result.push({ level: m[1].length, text: m[2] });
  }
  return result;
}

function extractWikiLinks(content: string): string[] {
  const links: string[] = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let m;
  while ((m = re.exec(content)) !== null) links.push(m[1]);
  return [...new Set(links)];
}

function extractTags(content: string): string[] {
  const tags: string[] = [];
  const re = /(^|\s)#([a-zA-Z][\w-]*)/gm;
  let m;
  while ((m = re.exec(content)) !== null) tags.push(m[2]);
  return [...new Set(tags)];
}

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function charCount(text: string): number {
  return text.length;
}

/* ═══════════════════════════════════════════════════════════════════
   AI Mock Responses
   ═══════════════════════════════════════════════════════════════════ */

const AI_RESPONSES: Record<string, string[]> = {
  summarize: [
    "This note covers key architectural decisions and their implications for the system design.",
    "The main points are: role-based access, composable ring architecture, and open-source alignment with OTEL patterns.",
    "Summary: This document outlines the core design patterns being adopted, with emphasis on extensibility and governance.",
  ],
  expand: [
    "Consider adding more detail about error handling across ring boundaries. What happens when the RBAC ring denies access mid-routing? How does the bridge handle partial failures?",
    "You could expand on the performance implications. Each ring adds latency — document the expected overhead and any caching strategies.",
  ],
  brainstorm: [
    "- What if each ring could operate in 'shadow mode' — logging decisions without enforcing them?\n- Consider a 'ring inspector' tool that visualizes request flow in real-time\n- Could the bridge support protocol negotiation, auto-selecting the most efficient transport?\n- Add support for 'ring plugins' that let teams add custom validation logic",
    "- A/B testing at the routing ring level\n- Cost prediction before execution\n- Automated compliance reports from RBAC audit logs\n- Agent reputation scores based on historical performance",
  ],
};

function mockAiResponse(prompt: string): string {
  const lower = prompt.toLowerCase();
  if (lower.includes("summarize") || lower.includes("summary")) {
    const arr = AI_RESPONSES.summarize;
    return arr[Math.floor(Math.random() * arr.length)];
  }
  if (lower.includes("expand") || lower.includes("detail") || lower.includes("elaborate")) {
    const arr = AI_RESPONSES.expand;
    return arr[Math.floor(Math.random() * arr.length)];
  }
  if (lower.includes("brainstorm") || lower.includes("ideas") || lower.includes("suggest")) {
    const arr = AI_RESPONSES.brainstorm;
    return arr[Math.floor(Math.random() * arr.length)];
  }
  return "I've analyzed the note content. The key themes here are architecture, governance, and system design. Would you like me to summarize, expand on a specific section, or brainstorm related ideas?";
}

/* ═══════════════════════════════════════════════════════════════════
   Mini Graph Component
   ═══════════════════════════════════════════════════════════════════ */

function MiniGraph({
  notes,
  activeNoteId,
  onSelect,
}: {
  notes: Note[];
  activeNoteId: string | null;
  onSelect: (id: string) => void;
}) {
  const width = 196;
  const height = 240;
  const cx = width / 2;
  const cy = height / 2;

  // Build adjacency from wiki links
  const titleToId = new Map<string, string>();
  notes.forEach((n) => titleToId.set(n.title, n.id));

  const edges: { from: string; to: string }[] = [];
  notes.forEach((n) => {
    const links = extractWikiLinks(n.content);
    links.forEach((linkTitle) => {
      const targetId = titleToId.get(linkTitle);
      if (targetId && targetId !== n.id) {
        edges.push({ from: n.id, to: targetId });
      }
    });
  });

  // Position nodes in a circle
  const nodePositions = new Map<string, { x: number; y: number }>();
  const radius = Math.min(width, height) * 0.36;
  notes.forEach((n, i) => {
    const angle = (2 * Math.PI * i) / notes.length - Math.PI / 2;
    nodePositions.set(n.id, {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    });
  });

  return (
    <svg className="nb-graph" viewBox={`0 0 ${width} ${height}`}>
      {/* Edges */}
      {edges.map((e, i) => {
        const from = nodePositions.get(e.from);
        const to = nodePositions.get(e.to);
        if (!from || !to) return null;
        const isActive = e.from === activeNoteId || e.to === activeNoteId;
        return (
          <line
            key={i}
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
            className="nb-graph__link"
            style={{
              stroke: isActive ? "rgba(79, 143, 255, 0.3)" : undefined,
              strokeWidth: isActive ? 1.5 : undefined,
            }}
          />
        );
      })}
      {/* Nodes */}
      {notes.map((n) => {
        const pos = nodePositions.get(n.id);
        if (!pos) return null;
        const isActive = n.id === activeNoteId;
        const hasLinks = edges.some((e) => e.from === n.id || e.to === n.id);
        return (
          <g
            key={n.id}
            className="nb-graph__node"
            onClick={() => onSelect(n.id)}
          >
            <circle
              cx={pos.x}
              cy={pos.y}
              r={isActive ? 7 : hasLinks ? 5 : 3.5}
              fill={isActive ? "#4f8fff" : hasLinks ? "rgba(79, 143, 255, 0.5)" : "rgba(255, 255, 255, 0.2)"}
              stroke={isActive ? "rgba(79, 143, 255, 0.6)" : "none"}
              strokeWidth={isActive ? 2 : 0}
              style={isActive ? { filter: "drop-shadow(0 0 8px rgba(79, 143, 255, 0.5))" } : undefined}
            />
            <text
              x={pos.x}
              y={pos.y + (isActive ? 16 : 14)}
              textAnchor="middle"
              style={{
                fill: isActive ? "#4f8fff" : undefined,
                fontWeight: isActive ? 600 : undefined,
              }}
            >
              {n.title.length > 16 ? n.title.slice(0, 15) + "\u2026" : n.title}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════ */

type ViewMode = "split" | "edit" | "preview";
type RightTab = "outline" | "graph" | "artifacts";

export function NotebookWorkbench() {
  const [notes, setNotes] = useState<Note[]>(loadNotes);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>("split");
  const [rightTab, setRightTab] = useState<RightTab>("outline");
  const [showNewModal, setShowNewModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | "idle">("idle");
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeNote = useMemo(() => notes.find((n) => n.id === activeNoteId) ?? null, [notes, activeNoteId]);

  // Persist notes
  useEffect(() => {
    saveNotes(notes);
  }, [notes]);

  // Select first note on mount if none active
  useEffect(() => {
    if (!activeNoteId && notes.length > 0) {
      setActiveNoteId(notes[0].id);
    }
  }, []);

  // All tags across all notes
  const allTags = useMemo(() => {
    const set = new Set<string>();
    notes.forEach((n) => n.tags.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [notes]);

  // Filtered notes
  const filteredNotes = useMemo(() => {
    let filtered = notes;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.content.toLowerCase().includes(q) ||
          n.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (filterTag) {
      filtered = filtered.filter((n) => n.tags.includes(filterTag));
    }
    return filtered;
  }, [notes, searchQuery, filterTag]);

  // Group by folder
  const notesByFolder = useMemo(() => {
    const map: Record<string, Note[]> = {};
    FOLDERS.forEach((f) => (map[f] = []));
    filteredNotes.forEach((n) => {
      if (map[n.folder]) map[n.folder].push(n);
      else map[n.folder] = [n];
    });
    // Sort within each folder by updatedAt desc
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    );
    return map;
  }, [filteredNotes]);

  // Backlinks for active note
  const backlinks = useMemo(() => {
    if (!activeNote) return [];
    return notes.filter(
      (n) => n.id !== activeNote.id && extractWikiLinks(n.content).includes(activeNote.title)
    );
  }, [notes, activeNote]);

  // Headings for active note
  const headings = useMemo(() => {
    if (!activeNote) return [];
    return extractHeadings(activeNote.content);
  }, [activeNote]);

  // Tags for active note
  const activeTags = useMemo(() => {
    if (!activeNote) return [];
    return extractTags(activeNote.content);
  }, [activeNote]);

  /* ── Handlers ── */

  const updateNoteContent = useCallback(
    (content: string) => {
      if (!activeNoteId) return;
      setSaveStatus("saving");

      setNotes((prev) =>
        prev.map((n) =>
          n.id === activeNoteId
            ? {
                ...n,
                content,
                tags: extractTags(content),
                updatedAt: new Date().toISOString(),
              }
            : n
        )
      );

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => setSaveStatus("saved"), 600);
    },
    [activeNoteId]
  );

  const createNote = useCallback(
    (title: string, folder: string) => {
      const note: Note = {
        id: generateId(),
        title,
        content: `# ${title}\n\n`,
        folder,
        tags: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setNotes((prev) => [note, ...prev]);
      setActiveNoteId(note.id);
      setShowNewModal(false);
      // Auto-expand folder
      setCollapsedFolders((prev) => {
        const next = new Set(prev);
        next.delete(folder);
        return next;
      });
    },
    []
  );

  const deleteNote = useCallback(
    (id: string) => {
      setNotes((prev) => prev.filter((n) => n.id !== id));
      if (activeNoteId === id) {
        setActiveNoteId(null);
      }
    },
    [activeNoteId]
  );

  const navigateToWikiLink = useCallback(
    (title: string) => {
      const target = notes.find((n) => n.title === title);
      if (target) setActiveNoteId(target.id);
    },
    [notes]
  );

  const handlePreviewClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("nb-wiki-link")) {
        const linkTitle = target.getAttribute("data-link");
        if (linkTitle) navigateToWikiLink(linkTitle);
      }
    },
    [navigateToWikiLink]
  );

  const insertToolbarAction = useCallback(
    (action: string) => {
      if (!editorRef.current || !activeNote) return;
      const ta = editorRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const selected = ta.value.substring(start, end);

      let insert = "";
      switch (action) {
        case "bold":
          insert = `**${selected || "bold text"}**`;
          break;
        case "italic":
          insert = `*${selected || "italic text"}*`;
          break;
        case "code":
          insert = selected.includes("\n")
            ? `\`\`\`\n${selected || "code"}\n\`\`\``
            : `\`${selected || "code"}\``;
          break;
        case "link":
          insert = `[[${selected || "note title"}]]`;
          break;
        case "heading":
          insert = `## ${selected || "Heading"}`;
          break;
        case "list":
          insert = `- ${selected || "List item"}`;
          break;
        case "quote":
          insert = `> ${selected || "Quote"}`;
          break;
        case "checkbox":
          insert = `- [ ] ${selected || "Task"}`;
          break;
        default:
          return;
      }

      const newContent =
        ta.value.substring(0, start) + insert + ta.value.substring(end);
      updateNoteContent(newContent);

      requestAnimationFrame(() => {
        ta.focus();
        const newPos = start + insert.length;
        ta.selectionStart = ta.selectionEnd = newPos;
      });
    },
    [activeNote, updateNoteContent]
  );

  const handleAiAsk = useCallback(() => {
    if (!aiInput.trim() || aiLoading || !activeNote) return;
    setAiLoading(true);

    // Simulate AI typing delay
    setTimeout(() => {
      const response = mockAiResponse(aiInput);
      const aiBlock = `\n\n> **AI** (${aiInput.trim()})\n> ${response.split("\n").join("\n> ")}\n`;
      const newContent = activeNote.content + aiBlock;
      updateNoteContent(newContent);
      setAiInput("");
      setAiLoading(false);
    }, 800 + Math.random() * 1200);
  }, [aiInput, aiLoading, activeNote, updateNoteContent]);

  const toggleFolder = useCallback((folder: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });
  }, []);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  /* ═══════════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════════ */

  return (
    <div className="nb-layout">
      {/* ── Left Sidebar ── */}
      <div className="nb-sidebar">
        <div className="nb-sidebar__header">
          <div className="nb-sidebar__title">
            <svg viewBox="0 0 24 24">
              <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
              <path d="M4 19.5V5a2 2 0 012-2h14v14H6.5A2.5 2.5 0 004 19.5z" />
            </svg>
            Notebook
          </div>
          <button
            className="nb-sidebar__new-btn"
            onClick={() => setShowNewModal(true)}
            title="New note"
          >
            +
          </button>
        </div>

        <div className="nb-sidebar__search">
          <input
            type="text"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="nb-sidebar__tree">
          {FOLDERS.map((folder) => {
            const folderNotes = notesByFolder[folder] || [];
            const isCollapsed = collapsedFolders.has(folder);
            if (folderNotes.length === 0 && searchQuery) return null;

            return (
              <div key={folder} className="nb-folder">
                <div
                  className="nb-folder__header"
                  onClick={() => toggleFolder(folder)}
                >
                  <span
                    className={`nb-folder__chevron ${!isCollapsed ? "nb-folder__chevron--open" : ""}`}
                  >
                    &#9654;
                  </span>
                  <svg className="nb-folder__icon" viewBox="0 0 24 24">
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                  </svg>
                  {folder}
                  <span style={{ marginLeft: "auto", fontSize: 9, color: "var(--ot-text-disabled)" }}>
                    {folderNotes.length}
                  </span>
                </div>
                {!isCollapsed &&
                  folderNotes.map((note) => (
                    <div
                      key={note.id}
                      className={`nb-note-item ${activeNoteId === note.id ? "nb-note-item--active" : ""}`}
                      onClick={() => setActiveNoteId(note.id)}
                    >
                      <div className="nb-note-item__content">
                        <div className="nb-note-item__title">{note.title}</div>
                        <div className="nb-note-item__meta">
                          <span className="nb-note-item__date">
                            {formatDate(note.updatedAt)}
                          </span>
                          {note.tags.slice(0, 2).map((t) => (
                            <span key={t} className="nb-note-item__tag">
                              #{t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>

        {/* Tags section */}
        <div className="nb-sidebar__tags">
          <div className="nb-sidebar__tags-label">Tags</div>
          <div className="nb-sidebar__tags-list">
            {allTags.map((tag) => (
              <button
                key={tag}
                className={`nb-sidebar__tag-pill ${filterTag === tag ? "nb-sidebar__tag-pill--active" : ""}`}
                onClick={() => setFilterTag(filterTag === tag ? null : tag)}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Center Panel (Editor) ── */}
      <div className="nb-editor-panel">
        {activeNote ? (
          <>
            {/* Toolbar */}
            <div className="nb-toolbar">
              <button
                className="nb-toolbar__btn"
                title="Bold"
                onClick={() => insertToolbarAction("bold")}
              >
                B
              </button>
              <button
                className="nb-toolbar__btn"
                title="Italic"
                onClick={() => insertToolbarAction("italic")}
                style={{ fontStyle: "italic" }}
              >
                I
              </button>
              <button
                className="nb-toolbar__btn"
                title="Code"
                onClick={() => insertToolbarAction("code")}
              >
                {"<>"}
              </button>
              <button
                className="nb-toolbar__btn"
                title="Wiki Link"
                onClick={() => insertToolbarAction("link")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                </svg>
              </button>
              <div className="nb-toolbar__sep" />
              <button
                className="nb-toolbar__btn"
                title="Heading"
                onClick={() => insertToolbarAction("heading")}
              >
                H
              </button>
              <button
                className="nb-toolbar__btn"
                title="List"
                onClick={() => insertToolbarAction("list")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
              <button
                className="nb-toolbar__btn"
                title="Quote"
                onClick={() => insertToolbarAction("quote")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="15" y2="6" />
                  <line x1="3" y1="18" x2="18" y2="18" />
                </svg>
              </button>
              <button
                className="nb-toolbar__btn"
                title="Checkbox"
                onClick={() => insertToolbarAction("checkbox")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </button>
              <div className="nb-toolbar__sep" />
              <button
                className={`nb-toolbar__btn nb-toolbar__btn--ai ${showAiPrompt ? "nb-toolbar__btn--active" : ""}`}
                onClick={() => setShowAiPrompt(!showAiPrompt)}
                title="Ask AI"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
                AI
              </button>

              <div className="nb-toolbar__spacer" />

              <div className="nb-toolbar__view-toggle">
                <button
                  className={`nb-toolbar__view-btn ${viewMode === "edit" ? "nb-toolbar__view-btn--active" : ""}`}
                  onClick={() => setViewMode("edit")}
                >
                  Edit
                </button>
                <button
                  className={`nb-toolbar__view-btn ${viewMode === "split" ? "nb-toolbar__view-btn--active" : ""}`}
                  onClick={() => setViewMode("split")}
                >
                  Split
                </button>
                <button
                  className={`nb-toolbar__view-btn ${viewMode === "preview" ? "nb-toolbar__view-btn--active" : ""}`}
                  onClick={() => setViewMode("preview")}
                >
                  Preview
                </button>
              </div>
            </div>

            {/* Editor area */}
            <div className={`nb-editor-area nb-editor-area--${viewMode}`}>
              {viewMode !== "preview" && (
                <textarea
                  ref={editorRef}
                  className="nb-editor__input"
                  value={activeNote.content}
                  onChange={(e) => updateNoteContent(e.target.value)}
                  placeholder="Start writing..."
                  spellCheck={false}
                />
              )}
              {viewMode !== "edit" && (
                <div
                  className={`nb-editor__preview ${viewMode === "preview" ? "nb-editor__preview--full" : ""}`}
                  onClick={handlePreviewClick}
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdownToHtml(activeNote.content),
                  }}
                />
              )}
            </div>

            {/* AI Prompt */}
            {showAiPrompt && (
              <div className="nb-ai-prompt">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ot-purple)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
                <input
                  type="text"
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAiAsk();
                    if (e.key === "Escape") setShowAiPrompt(false);
                  }}
                  placeholder="Ask AI about this note... (summarize, expand, brainstorm)"
                  autoFocus
                  disabled={aiLoading}
                />
                <button
                  className="nb-ai-prompt__send"
                  onClick={handleAiAsk}
                  disabled={aiLoading || !aiInput.trim()}
                  style={{ opacity: aiLoading || !aiInput.trim() ? 0.5 : 1 }}
                >
                  {aiLoading ? "Thinking..." : "Ask"}
                </button>
                <button
                  className="nb-ai-prompt__close"
                  onClick={() => setShowAiPrompt(false)}
                >
                  ×
                </button>
              </div>
            )}

            {/* Footer */}
            <div className="nb-footer">
              <div className="nb-footer__stats">
                <span>{wordCount(activeNote.content)} words</span>
                <span>{charCount(activeNote.content)} chars</span>
              </div>
              <div
                className={`nb-footer__save ${
                  saveStatus === "saved"
                    ? "nb-footer__save--saved"
                    : saveStatus === "saving"
                      ? "nb-footer__save--saving"
                      : ""
                }`}
              >
                {saveStatus === "saved" && (
                  <>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    Saved
                  </>
                )}
                {saveStatus === "saving" && "Saving..."}
              </div>
            </div>
          </>
        ) : (
          <div className="nb-empty">
            <svg className="nb-empty__icon" viewBox="0 0 24 24">
              <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
              <path d="M4 19.5V5a2 2 0 012-2h14v14H6.5A2.5 2.5 0 004 19.5z" />
              <line x1="9" y1="7" x2="15" y2="7" />
              <line x1="9" y1="11" x2="13" y2="11" />
            </svg>
            <span className="nb-empty__text">Select a note to start editing</span>
            <span className="nb-empty__hint">
              or press + to create a new one
            </span>
          </div>
        )}
      </div>

      {/* ── Right Panel ── */}
      <div className="nb-right-panel">
        <div className="nb-right-panel__tabs">
          <button
            className={`nb-right-panel__tab ${rightTab === "outline" ? "nb-right-panel__tab--active" : ""}`}
            onClick={() => setRightTab("outline")}
          >
            Outline
          </button>
          <button
            className={`nb-right-panel__tab ${rightTab === "graph" ? "nb-right-panel__tab--active" : ""}`}
            onClick={() => setRightTab("graph")}
          >
            Graph
          </button>
          <button
            className={`nb-right-panel__tab ${rightTab === "artifacts" ? "nb-right-panel__tab--active" : ""}`}
            onClick={() => setRightTab("artifacts")}
          >
            Links
          </button>
        </div>

        <div className="nb-right-panel__content">
          {rightTab === "artifacts" ? (
            <div className="nb-artifacts">
              <div className="nb-outline__section">
                <div className="nb-outline__section-label">Workspace Watchlist</div>
                <div className="nb-artifacts__intro">
                  Published internal URLs and architecture artifacts worth watching while work moves.
                </div>
              </div>
              {ARTIFACT_LINKS.map((link) => (
                <a
                  key={link.url}
                  className="nb-artifacts__item"
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="nb-artifacts__meta">{link.group}</div>
                  <div className="nb-artifacts__title">{link.title}</div>
                  <div className="nb-artifacts__note">{link.note}</div>
                  <div className="nb-artifacts__url">{link.url}</div>
                </a>
              ))}
            </div>
          ) : rightTab === "outline" && activeNote ? (
            <>
              {/* Headings */}
              {headings.length > 0 && (
                <div className="nb-outline__section">
                  <div className="nb-outline__section-label">Headings</div>
                  {headings.map((h, i) => (
                    <button
                      key={i}
                      className={`nb-outline__heading nb-outline__heading--h${h.level}`}
                      onClick={() => {
                        // Scroll to heading in editor
                        if (editorRef.current) {
                          const idx = activeNote.content.indexOf(`${"#".repeat(h.level)} ${h.text}`);
                          if (idx >= 0) {
                            editorRef.current.focus();
                            editorRef.current.selectionStart = idx;
                            editorRef.current.selectionEnd = idx;
                          }
                        }
                      }}
                    >
                      {"#".repeat(h.level)} {h.text}
                    </button>
                  ))}
                </div>
              )}

              {/* Backlinks */}
              {backlinks.length > 0 && (
                <div className="nb-outline__section">
                  <div className="nb-outline__section-label">
                    Backlinks ({backlinks.length})
                  </div>
                  {backlinks.map((bl) => (
                    <button
                      key={bl.id}
                      className="nb-outline__backlink"
                      onClick={() => setActiveNoteId(bl.id)}
                    >
                      {bl.title}
                    </button>
                  ))}
                </div>
              )}

              {/* Tags */}
              {activeTags.length > 0 && (
                <div className="nb-outline__section">
                  <div className="nb-outline__section-label">Tags</div>
                  <div>
                    {activeTags.map((t) => (
                      <span key={t} className="nb-outline__tag">
                        #{t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {!headings.length && !backlinks.length && !activeTags.length && (
                <div
                  style={{
                    color: "var(--ot-text-muted)",
                    fontSize: 11,
                    textAlign: "center",
                    padding: "24px 0",
                  }}
                >
                  No outline data
                </div>
              )}
            </>
          ) : rightTab === "outline" && !activeNote ? (
            <div
              style={{
                color: "var(--ot-text-muted)",
                fontSize: 11,
                textAlign: "center",
                padding: "24px 0",
              }}
            >
              Select a note
            </div>
          ) : (
            <MiniGraph
              notes={notes}
              activeNoteId={activeNoteId}
              onSelect={setActiveNoteId}
            />
          )}
        </div>
      </div>

      {/* ── New Note Modal ── */}
      {showNewModal && <NewNoteModal onCreate={createNote} onClose={() => setShowNewModal(false)} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   New Note Modal
   ═══════════════════════════════════════════════════════════════════ */

function NewNoteModal({
  onCreate,
  onClose,
}: {
  onCreate: (title: string, folder: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [folder, setFolder] = useState<string>("Projects");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCreate = () => {
    if (!title.trim()) return;
    onCreate(title.trim(), folder);
  };

  return (
    <div className="nb-modal-overlay" onClick={onClose}>
      <div className="nb-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nb-modal__title">New Note</div>

        <div className="nb-modal__field">
          <label className="nb-modal__label">Title</label>
          <input
            ref={inputRef}
            className="nb-modal__input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
              if (e.key === "Escape") onClose();
            }}
            placeholder="Note title..."
          />
        </div>

        <div className="nb-modal__field">
          <label className="nb-modal__label">Folder</label>
          <select
            className="nb-modal__select"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
          >
            {FOLDERS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>

        <div className="nb-modal__actions">
          <button className="ot-btn ot-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="ot-btn ot-btn-primary"
            onClick={handleCreate}
            disabled={!title.trim()}
            style={{ opacity: title.trim() ? 1 : 0.5 }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
